import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface RegistrationFormProps {
  onRegister: (userData: {
    name: string;
    email: string;
    company: string;
    street: string;
    city: string;
    postalCode: string;
    voucher?: string;
    agbAccepted: boolean;
    password: string;
  }) => Promise<void>;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({ onRegister }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    street: '',
    city: '',
    postalCode: '',
    voucher: '',
    password: '',
    confirmPassword: '',
    agbAccepted: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [voucherValidation, setVoucherValidation] = useState<{
    status: 'idle' | 'checking' | 'valid' | 'invalid';
    message: string;
  }>({ status: 'idle', message: '' });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || !formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (!formData.email || !formData.email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }

    if (!formData.company || !formData.company.trim()) {
      newErrors.company = 'Unternehmen ist erforderlich';
    }

    if (!formData.street || !formData.street.trim()) {
      newErrors.street = 'Straße ist erforderlich';
    }

    if (!formData.city || !formData.city.trim()) {
      newErrors.city = 'Ort ist erforderlich';
    }

    if (!formData.postalCode || !formData.postalCode.trim()) {
      newErrors.postalCode = 'PLZ ist erforderlich';
    } else if (!/^\d{5}$/.test(formData.postalCode.trim())) {
      newErrors.postalCode = 'PLZ muss 5 Ziffern enthalten';
    }

    if (!formData.password || !formData.password.trim()) {
      newErrors.password = 'Passwort ist erforderlich';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Passwort muss mindestens 8 Zeichen lang sein';
    }

    if (!formData.confirmPassword || !formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Passwort bestätigen ist erforderlich';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwörter stimmen nicht überein';
    }

    if (!formData.agbAccepted) {
      newErrors.agb = 'Bitte akzeptieren Sie die AGB';
    }

    if (formData.voucher && voucherValidation.status === 'invalid') {
      newErrors.voucher = 'Bitte geben Sie einen gültigen Gutscheincode ein oder lassen Sie das Feld leer';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateVoucher = async (voucherCode: string) => {
    if (!voucherCode.trim()) {
      setVoucherValidation({ status: 'idle', message: '' });
      return;
    }

    setVoucherValidation({ status: 'checking', message: 'Gutschein wird überprüft...' });

    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/validate-voucher`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          code: voucherCode.trim()
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setVoucherValidation({
          status: 'invalid',
          message: errorData.error || 'Gutscheincode ist ungültig oder nicht mehr verfügbar'
        });
        return;
      }

      const result = await response.json();

      if (!result.valid) {
        setVoucherValidation({
          status: 'invalid',
          message: result.error || 'Gutscheincode ist ungültig'
        });
        return;
      }

      const voucher = result.voucher;
      const discountText = voucher.discount_type === 'percentage'
        ? `${voucher.discount_value}% Rabatt`
        : `${voucher.discount_value}€ Rabatt`;

      setVoucherValidation({
        status: 'valid',
        message: `✓ Gültiger Gutschein: ${discountText}`
      });

    } catch (error) {
      console.error('Error validating voucher:', error);
      setVoucherValidation({
        status: 'invalid',
        message: 'Fehler bei der Überprüfung des Gutscheins. Bitte versuchen Sie es später erneut.'
      });
    }
  };

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.voucher !== voucherValidation.message) {
        validateVoucher(formData.voucher);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.voucher]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (name === 'voucher') {
      setVoucherValidation({ status: 'idle', message: '' });
    }

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await onRegister(formData);
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({
        form: 'Bei der Registrierung ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Registrierung</h2>

      {errors.form && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md" role="alert">
          {errors.form}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-4">Persönliche Informationen</h3>

            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  E-Mail <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                  Unternehmen <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.company ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.company && (
                  <p className="mt-1 text-sm text-red-600">{errors.company}</p>
                )}
              </div>

              <div>
                <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-1">
                  Straße und Hausnummer <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="street"
                  name="street"
                  value={formData.street}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.street ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="z.B. Musterstraße 123"
                />
                {errors.street && (
                  <p className="mt-1 text-sm text-red-600">{errors.street}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                    PLZ <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.postalCode ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="12345"
                    maxLength={5}
                  />
                  {errors.postalCode && (
                    <p className="mt-1 text-sm text-red-600">{errors.postalCode}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                    Ort <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.city ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="z.B. Berlin"
                  />
                  {errors.city && (
                    <p className="mt-1 text-sm text-red-600">{errors.city}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rechnungsadresse Vorschau
                </label>
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600">
                  {formData.street || 'Straße und Hausnummer'}<br />
                  {formData.postalCode || 'PLZ'} {formData.city || 'Ort'}
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Passwort <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Mindestens 8 Zeichen"
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Passwort bestätigen <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Passwort wiederholen"
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                )}
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-4">Zahlung</h3>

            <div className="space-y-4">
              <div>
                <label htmlFor="voucher" className="block text-sm font-medium text-gray-700 mb-1">
                  Gutscheincode (optional)
                </label>
                <input
                  type="text"
                  id="voucher"
                  name="voucher"
                  value={formData.voucher}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    voucherValidation.status === 'valid'
                      ? 'border-green-500 bg-green-50'
                      : voucherValidation.status === 'invalid'
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-300'
                  }`}
                  placeholder="z.B. WELCOME50"
                />
                {voucherValidation.message && (
                  <p className={`mt-1 text-sm ${
                    voucherValidation.status === 'valid'
                      ? 'text-green-600'
                      : voucherValidation.status === 'invalid'
                        ? 'text-red-600'
                        : 'text-blue-600'
                  }`}>
                    {voucherValidation.status === 'checking' && (
                      <span className="inline-flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {voucherValidation.message}
                      </span>
                    )}
                    {voucherValidation.status !== 'checking' && voucherValidation.message}
                  </p>
                )}
                {errors.voucher && (
                  <p className="mt-1 text-sm text-red-600">{errors.voucher}</p>
                )}
                {formData.voucher && voucherValidation.status === 'valid' && (
                  <p className="mt-1 text-sm text-gray-500">
                    Gutschein wird bei der Zahlung automatisch angewendet
                  </p>
                )}
              </div>

              <div className="p-4 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-600">
                  Die Zahlung erfolgt per Kreditkarte. Nach der Registrierung werden Sie zur sicheren Zahlungsseite weitergeleitet.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  id="agbAccepted"
                  name="agbAccepted"
                  checked={formData.agbAccepted}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
              <div className="ml-3">
                <label htmlFor="agbAccepted" className="text-sm text-gray-700">
                  Ich akzeptiere die <Link to="/agb" className="text-blue-600 hover:underline" target="_blank">AGB</Link> <span className="text-red-500">*</span>
                </label>
                {errors.agb && (
                  <p className="mt-1 text-sm text-red-600">{errors.agb}</p>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-4 rounded-md transition-colors ${
                loading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Registrierung läuft...' : 'Jetzt registrieren'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;
